import { IAddressV2 } from './address';
import { IOrder } from './order';
import { PaymentMethodsEnum, PaymentMethodSource, PSPEnum } from './payment';
import { IPrismicImage, IPrismicRichText } from './prismic';
import { ShippingMethodTypes } from './shippingMethods';

export const isCheckoutProductError = (
  maybeError: unknown,
): maybeError is ICheckoutProductError =>
  maybeError != null &&
  typeof maybeError === 'object' &&
  'sku' in maybeError &&
  'error' in maybeError &&
  'message' in maybeError;

export interface ICheckoutProductError {
  sku: string;
  error: string;
  message: string;
}

export interface ICheckoutCartItem {
  name: string;
  quantity: number;
  unitPrice: number;
  originalPrice: number;
  total: number;
  sku: string;
  variantCode: string;
  variantOriginalPrice: number;
  variantWeight: number;
  variantShippingRequired: boolean;
  subtotal: number;
  productVendor: string;
  productImage: string;
}

interface IShipmentMethodTranslations {
  fr_FR: string;
}

export interface IShipment {
  id: string;
  code: string;
  translations: IShipmentMethodTranslations | null;
  address?: IAddressV2 | null;
  pointId?: string | null;
  deliveryDate?: string | null;
  timeSlot?: string | null;
  deliveryInstruction?: string | null;
  type?: string;
  __typename?: string;
}

interface IPayment {
  id: string;
  financialPaymentIntentClientSecret?: string;
  paypalOrderId?: string;
  financialPaymentRedirectLink?: string;
  financialPaymentIntentId?: string;
  /** Only populated by confirmFlowPayment: the method resolved from Checkout.com's source type. */
  paymentMethodType?: PaymentMethodsEnum;
}

export interface IPaymentMethod {
  id: string;
  code: string;
  name: string;
  publishableKey?: string;
  /** @deprecated use psp or method instead */
  type: PSPEnum;
  psp: PSPEnum;
  method: PaymentMethodsEnum;
}

export interface IListPaymentMethodsQueryResult {
  listPaymentMethods: IPaymentMethod[];
}

export interface IPaymentIntentError {
  errorKey: string;
  errorMessage?: string;
}

export interface IPaymentIntentState {
  state: null | 'success' | 'error';
  error: null | IPaymentIntentError;
}

export interface IUsePaymentIntentReturn extends IPaymentIntentState {
  handlePayment: (props: {
    paymentMethodCode: string;
    paymentToken?: string;
  }) => void;
  isLoading: boolean;
}

interface IExistingPaymentMethod {
  paymentMethodId: string;
}

interface INewPaymentMethod {
  newPaymentMethod: string | null;
  paymentMethodSource: PaymentMethodSource;
  saveCardForFutureUse: boolean;
}

export type GetPaymentMethodReturnType =
  | IExistingPaymentMethod
  | INewPaymentMethod
  | undefined;

export enum CheckoutStateEnum {
  CART = 'cart',
  ADDRESSED = 'addressed',
  SHIPPING_SELECTED = 'shipping_selected',
  SHIPPING_SKIPPED = 'shipping_skipped',
  PAYMENT_SELECTED = 'payment_selected',
  PAYMENT_SKIPPED = 'payment_skipped',
  COMPLETED = 'completed',
}

export enum SourceEnum {
  GOOGLE_SHOPPING = 'google_shopping',
}

export enum CheckoutAppEnum {
  WEB = 'web',
  MOBILE = 'mobile',
}

export const CheckoutOriginPlatforms = {
  WEB: 'web',
  UNKNOWN: 'unknown',
  IOS: 'ios',
  ANDROID: 'android',
};

export type CheckoutOriginPlatform =
  (typeof CheckoutOriginPlatforms)[keyof typeof CheckoutOriginPlatforms];

export interface ICheckoutCart {
  id: string;
  checkoutUrl: string;
  currencyCode: string;
  items: ICheckoutCartItem[];
  subTotal: number;
  taxTotal: number;
  total: number;
  totalItems: number;
  shipments: IShipment[];
  payment?: IPayment;
  orderPromotionTotal: number;
  shippingTotal: number;
  checkoutState: CheckoutStateEnum;
  shippingAddress: IAddressV2;
  billingAddress: IAddressV2;
  promotionCoupon?: IPromotionCoupon;
  customerMemberType: string;
  source?: SourceEnum.GOOGLE_SHOPPING;
  isFresh?: boolean;
  isUltraFresh?: boolean;
  isProspecting: boolean;
  isRoundUpApplied: boolean;
  roundUpAmount?: number;
  withMembership: boolean;
  subscriptionId?: string;
  totalPfand?: number;
  hasUltraFreshProducts?: boolean;
  __typename?: string;
}

export interface ICountries {
  code: string;
  provinces: string[];
}

export interface IPromotionCoupon {
  code: string;
  id: string;
}
export interface IChannelCountry {
  code: string;
}

export interface IAddShippingAndBillingAddressesQueryResult {
  addShippingAndBillingAddresses: ICheckoutCart;
}

export interface IAddBillingAddressQueryResult {
  addBillingAddress: ICheckoutCart;
}

export interface IGetCountriesQueryResult {
  getCountries: ICountries[];
}

export interface IGetChannelResourceQueryResult {
  getChannelResource: {
    countries: IChannelCountry[];
  };
}

export interface IGetCustomerOrderQueryResult {
  getCustomerOrder: {
    items: IOrder[];
    cursor: string;
  };
}

export interface IGetChannelPaymentMethodsQueryResult {
  getChannelResource: {
    paymentMethods: IPaymentMethod[];
  };
}

export interface IPickupPoint {
  dayOfWeek: Array<{
    day: number;
    hours: string[];
  }>;
  pointId: string;
  pointName: string;
  street: string;
  zip: string;
  city: string;
  courier: string;
  latitude: number;
  longitude: number;
  distanceFromOrigin: { value: string };
  imageURL: string;
}

export interface IShippingMethod {
  id: string;
  code: string;
  tmsCode: string;
  price: number;
  type: ShippingMethodTypes;
  translations: {
    fr_FR: {
      name: string;
      description: string;
    };
  };
}

export interface IListShippingMethodsQueryResult {
  listShippingMethods: IShippingMethod[];
}

export enum NotificationEnum {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}

export interface ICheckoutNotification {
  type: NotificationEnum;
  translatedMessage: string;
  details: {
    sku?: string;
  };
}

export interface ICheckoutPageContent {
  featureFlagKey?: string;
  shippingWarningTagContext: string;
  shippingWarningMessage: IPrismicRichText;
  shippingWarningMessagePicto: IPrismicImage;
  paymentWarningMessage: IPrismicRichText;
  paymentWarningMessagePicto: IPrismicImage;
  paymentText?: string;
  paymentDisclaimer?: string;
  paymentTextMonthly?: string;
  paymentTextYearly?: string;
}

// WARNING !!! all responses below which are type ICheckoutCart does not contain all ICheckoutCart props.
// To see specific props go to graphql related query

export interface ICreateCartQueryResultData {
  cart: ICheckoutCart;
  notifications?: Array<ICheckoutNotification>;
  errors?: Array<ICheckoutProductError>;
}

export interface ICreateCartQueryResult {
  createCart: ICreateCartQueryResultData;
}

export interface ICreatePaymentIntentQueryResult {
  createPaymentIntent: ICheckoutCart;
}
export interface ICreatePaymentIntentForExistingPMQueryResult {
  createPaymentIntentForExistingPM: ICheckoutCart;
}
export interface ICreatePaymentIntentForNewCardQueryResult {
  createPaymentIntentForNewCard: ICheckoutCart;
}
export interface ICreatePaymentForNewPaypalIntentQueryResult {
  createPaymentIntentForNewPaypal: ICheckoutCart;
}
export interface ICreatePaymentForNewPaypalAgreementIntentQueryResult {
  createPaymentIntentForNewPaypalAgreement: ICheckoutCart;
}
export interface ICreatePaymentIntentForNewApplePayCardQueryResult {
  createPaymentIntentForNewApplePayCard: ICheckoutCart;
}

export interface ICheckoutPaymentSession {
  id: string;
  paymentSessionToken: string;
}

export interface ICreateCheckoutPaymentSessionQueryResult {
  createCheckoutPaymentSession: ICheckoutPaymentSession;
}

export interface ICreateCheckoutFlowPaymentSessionQueryResult {
  createCheckoutFlowPaymentSession: ICheckoutPaymentSession;
}

export interface ICreateSubscriptionPaymentSessionQueryResult {
  createSubscriptionPaymentSession: ICheckoutPaymentSession;
}

export interface IConfirmFlowPaymentQueryResult {
  confirmFlowPayment: ICheckoutCart;
}

export interface ICompleteOrderQueryResult {
  completeOrder: ICheckoutCart;
}

export interface ISelectCourierShippingMethodQueryResult {
  selectCourierShippingMethod: Partial<ICheckoutCart>;
}

export interface ISelectHomeShippingMethodQueryResult {
  selectHomeShippingMethod: Partial<ICheckoutCart>;
}

export interface ISelectPickupShippingMethodQueryResult {
  selectPickupShippingMethod: Partial<ICheckoutCart>;
}

// END WARNING

export interface ICreatePspTokenFromApplePayTokenQueryResult {
  createPspTokenFromApplePayToken: {
    token: string;
  };
}

export interface IShippingMethodInfos {
  shipmentMethodCode: string | null;
  deliveryDate?: string;
  timeSlot?: string;
  deliveryInstruction?: string;
  pointId?: string;
  address?: IAddressV2;
}

export interface ICreatePspTokenFromApplePayTokenResult {
  createPspTokenFromApplePayToken: {
    token: string;
  };
}

export interface IPromotionCoupon {
  promotionCoupon: ICoupon[];
}

export interface ICoupon {
  code?: string;
  threshold?: number;
  locale: string;
  result: IResult;
}

interface IResult {
  success: string;
  error: string;
}

export type PickupPointStorageType = {
  pickupName: string;
  pickupStreet: string;
  pickupCity: string;
  pickupZipCode: string;
};
